import React, { useCallback, useEffect, useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { withTransaction } from '@elastic/apm-rum-react';
import { useTheme } from '@mui/material/styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useFormik } from 'formik';
import { string, object, number } from 'yup';
import useOrganization from '../../hooks/UseOrganization';
import REGEX from '../../const/Regex';

/**
 * @function EmailSetting
 * @description This component is used for managing email settings within an organization.
 * It fetches the existing email configuration for the organization and allows the user to update or create new email settings.
 * @param {object} state - The state object containing the mode, common and other relevant properties. This state object is passed from the parent component.
 * @returns {JSX} renders EmailSetting UI
 * @example <EmailSetting state={state} />
 */
const EmailSetting = () => {
  const { getEmailRequest, createEmailRequest, updateEmailRequest } =
    useOrganization();
  const orgName = useSelector((state) => state.common.orgName);
  const theme = useTheme();
  const [activeEmailData, setActiveEmailData] = useState(null);
  const [hidden, setHidden] = useState(true);
  const MASKED_PASSWORD = '********';

  const formikForm = useFormik({
    initialValues: {
      email: '',
      display_name: '',
      reply_to: '',
      host: '',
      port: '',
      user: '',
      password: '',
      organization: '',
    },
    validationSchema: object({
      email: string()
        .matches(REGEX.EMAIL, 'Email address is invalid')
        .email('Email address is invalid')
        .max(150, 'Email must be at most 150 characters')
        .required('Required'),
      display_name: string()
        .min(2, 'Display name must be at least 2 characters')
        .max(150, 'Display name must be at most 150 characters')
        .matches(REGEX.DISPLAY_NAME, 'Invalid display name')
        .required('Required'),
      reply_to: string()
        .matches(REGEX.EMAIL, 'Email address is invalid')
        .max(150, 'Reply to must be at most 150 characters')
        .email('Email address is invalid')
        .required('Required'),
      host: string()
        .matches(REGEX.NO_SPACE_IN_START_AND_END, 'Invalid host')
        .max(253, 'Host must be at most 253 characters')
        .required('Required'),
      port: number()
        .max(65535, 'Port must be less than or equal to 65535')
        .integer('Port must be an integer')
        .min(0, 'Port must be greater than or equal to 0')
        .required('Required'),
      user: string()
        .matches(REGEX.NO_SPACE_IN_START_AND_END, 'Invalid user')
        .min(2, 'User must be at least 2 characters')
        .max(1500, 'User must be at most 1500 characters')
        .required('Required'),
      password: string()
        .max(1500, 'Password must be at most 1500 characters')
        .matches(REGEX.NO_SPACE_IN_START_AND_END, 'Invalid password')
        .required('Required'),
    }),
    onSubmit: (values) => {
      const payload = {
        ...values,
        password:
          values.password !== MASKED_PASSWORD
            ? values.password
            : activeEmailData?.password,
        organization: orgName,
      };
      if (activeEmailData) {
        updateEmailRequest(orgName, payload);
      } else {
        createEmailRequest(orgName, payload).then(() => {
          getEmailRequest(orgName).then((res) => {
            setActiveEmailData(res?.data);
          });
        });
      }
    },
  });

  useEffect(() => {
    getEmailRequest(orgName).then((resp) => {
      if (Object.keys(resp?.data).length > 0) {
        setActiveEmailData(resp?.data);
        formikForm.setValues({
          email: resp?.data?.email,
          display_name: resp?.data?.display_name,
          reply_to: resp?.data?.reply_to,
          host: resp?.data?.host,
          port: resp?.data?.port,
          password: MASKED_PASSWORD,
          user: resp?.data?.user,
        });
      } else {
        setActiveEmailData(null);
        formikForm.resetForm();
      }
    });
  }, [orgName]);

  /**
   * @function handleChangeHidden
   * @description Toggles the visibility of the password field. This function is used to switch between showing and hiding the password in the password input field.
   * @returns {void}
   * @example handleChangeHidden();
   */
  const handleChangeHidden = useCallback(
    () => setHidden((prev) => !prev),
    [setHidden]
  );

  /**
   * @function handleMouseDownPassword
   * @description This function ensures that the default action (such as focusing the input field) does not occur when the user clicks the icon to toggle password visibility.
   * @param {object} event - The event object associated with the mouse down action.
   * @returns {void}
   * @example handleMouseDownPassword(event);
   */
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <Grid padding="20px">
      <Grid container alignItems="center" item xs={12}>
        <Typography
          variant="h3"
          color={theme.palette.text.primary}
          flexGrow="1"
        >
          Email Settings
        </Typography>
      </Grid>

      <Grid container direction="row" spacing={2} paddingTop={2} xs={12}>
        <Grid item md={4} sm={4} xs={12}>
          <TextField
            fullWidth
            required
            label="Email"
            type="email"
            placeholder="Enter email"
            name="email"
            value={formikForm.values.email}
            onChange={formikForm.handleChange}
            aria-label="email"
            onBlur={formikForm.handleBlur}
            error={formikForm.touched.email && Boolean(formikForm.errors.email)}
            helperText={formikForm.touched.email && formikForm.errors.email}
          />
        </Grid>
        <Grid item md={4} sm={4} xs={12}>
          <TextField
            fullWidth
            required
            label="Display name"
            type="text"
            placeholder="Enter display name"
            name="display_name"
            value={formikForm.values.display_name}
            onChange={formikForm.handleChange}
            aria-label="enterDisplayName"
            onBlur={formikForm.handleBlur}
            error={
              formikForm.touched.display_name &&
              Boolean(formikForm.errors.display_name)
            }
            helperText={
              formikForm.touched.display_name && formikForm.errors.display_name
            }
            InputProps={{
              endAdornment: (
                <Tooltip
                  title={
                    <Typography>
                      - Display name should start with a character. <br />
                      - Numbers (0-9) are allowed. <br />- Underscore(_) and
                      hyphen (-) are allowed.
                      <br />- Spaces are not allowed.
                    </Typography>
                  }
                  arrow
                  placement="top"
                >
                  <InfoOutlinedIcon />
                </Tooltip>
              ),
            }}
          />
        </Grid>
        <Grid item md={4} sm={4} xs={12}>
          <TextField
            fullWidth
            required
            label="Reply to"
            type="email"
            placeholder="Enter client email ID"
            name="reply_to"
            value={formikForm.values.reply_to}
            onChange={formikForm.handleChange}
            aria-label="replyTo"
            onBlur={formikForm.handleBlur}
            error={
              formikForm.touched.reply_to && Boolean(formikForm.errors.reply_to)
            }
            helperText={
              formikForm.touched.reply_to && formikForm.errors.reply_to
            }
          />
        </Grid>
        <Grid item md={4} sm={4} xs={12}>
          <TextField
            fullWidth
            required
            label="Host"
            type="text"
            placeholder="Enter host"
            name="host"
            value={formikForm.values.host}
            onChange={formikForm.handleChange}
            aria-label="enterHost"
            onBlur={formikForm.handleBlur}
            error={formikForm.touched.host && Boolean(formikForm.errors.host)}
            helperText={formikForm.touched.host && formikForm.errors.host}
          />
        </Grid>
        <Grid item md={4} sm={4} xs={12}>
          <TextField
            fullWidth
            required
            label="Port"
            type="number"
            placeholder="Enter port number"
            onKeyDown={(e) =>
              ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()
            }
            name="port"
            value={formikForm.values.port}
            onChange={formikForm.handleChange}
            aria-label="enterPortNumber"
            onBlur={formikForm.handleBlur}
            error={formikForm.touched.port && Boolean(formikForm.errors.port)}
            helperText={formikForm.touched.port && formikForm.errors.port}
          />
        </Grid>
        <Grid item md={4} sm={4} xs={12}>
          <TextField
            fullWidth
            required
            label="User"
            type="text"
            placeholder="Enter username"
            name="user"
            value={formikForm.values.user}
            onChange={formikForm.handleChange}
            aria-label="enterUserName"
            onBlur={formikForm.handleBlur}
            error={formikForm.touched.user && Boolean(formikForm.errors.user)}
            helperText={formikForm.touched.user && formikForm.errors.user}
          />
        </Grid>
        <Grid item md={4} sm={4} xs={12}>
          <TextField
            fullWidth
            required
            label="Password"
            placeholder="Enter password"
            type={!hidden ? 'text' : 'password'}
            name="password"
            value={formikForm.values.password}
            onChange={formikForm.handleChange}
            aria-label="enterPassword"
            onBlur={formikForm.handleBlur}
            error={
              formikForm.touched.password && Boolean(formikForm.errors.password)
            }
            helperText={
              formikForm.touched.password && formikForm.errors.password
            }
            sx={(_theme) => {
              return {
                '& .MuiFilledInput-root': {
                  backgroundColor: `${_theme.palette.other.blue2} !important`,
                },
              };
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    color="primary"
                    aria-label="toggle credential visibility"
                    onClick={handleChangeHidden}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    size="large"
                  >
                    {!hidden ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      <Grid container justifyContent="flex-start" mt={3}>
        <Box mr="12px">
          <Button
            variant="outlined"
            onClick={formikForm.handleReset}
            title="Reset"
          >
            Reset
          </Button>
        </Box>
        <Box>
          <Button
            variant="contained"
            onClick={formikForm.handleSubmit}
            title="Submit"
          >
            Submit
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default withTransaction('EmailSetting', 'component')(EmailSetting);
